import React from "react";
import type { LeaveSaveOutcome, SaveBeforeLeaveResult } from "../core/saveBeforeLeaveOutcome";
import { UnsavedChangesGuardProvider } from "../react/UnsavedChangesGuardContext";
import { useUnsavedChangesBlocker } from "../react/useUnsavedChangesBlocker";
import { useFormikUnsavedGuardConfig } from "./useFormikUnsavedGuardConfig";

export type FormikUnsavedChangesGuardProps = {
    children: React.ReactNode;
    /** When true, blocks sidebar/breadcrumb/back and other route changes via useBlocker. */
    enableRouteBlocking?: boolean;
    readOnlyForm?: boolean;
    enableUnsavedChangesGuard?: boolean;
    isNewRecord?: boolean;
    newRecordLabel?: string;
    unsavedChangesTitle?: string;
    unsavedChangesMessage?: string;
    unsavedChangesCancelLabel?: string;
    unsavedChangesContinueWithoutSavingLabel?: string;
    enableSaveBeforeLeave?: boolean;
    leaveAfterSaveRef?: React.MutableRefObject<boolean>;
    saveOutcomeRef?: React.MutableRefObject<LeaveSaveOutcome>;
    onSaveBeforeLeave?: () => Promise<SaveBeforeLeaveResult>;
};

function UnsavedChangesRouteBlocker({ when }: { when: boolean }) {
    useUnsavedChangesBlocker({ when });
    return null;
}

export function FormikUnsavedChangesGuard({
    children,
    enableRouteBlocking = false,
    readOnlyForm = false,
    enableUnsavedChangesGuard = true,
    isNewRecord = false,
    newRecordLabel,
    unsavedChangesTitle,
    unsavedChangesMessage,
    unsavedChangesCancelLabel,
    unsavedChangesContinueWithoutSavingLabel,
    enableSaveBeforeLeave = false,
    leaveAfterSaveRef,
    saveOutcomeRef,
    onSaveBeforeLeave: onSaveBeforeLeaveProp,
}: FormikUnsavedChangesGuardProps) {
    const guardConfig = useFormikUnsavedGuardConfig({
        readOnlyForm,
        enableUnsavedChangesGuard,
        isNewRecord,
        newRecordLabel,
        unsavedChangesTitle,
        unsavedChangesMessage,
        unsavedChangesCancelLabel,
        unsavedChangesContinueWithoutSavingLabel,
        enableSaveBeforeLeave,
        leaveAfterSaveRef,
        saveOutcomeRef,
        onSaveBeforeLeave: onSaveBeforeLeaveProp,
    });

    return (
        <UnsavedChangesGuardProvider
            isDirty={guardConfig.isDirty}
            onSave={guardConfig.onSave}
            cancelLabel={guardConfig.cancelLabel}
            title={guardConfig.title}
            message={guardConfig.message}
            continueWithoutSavingLabel={guardConfig.continueWithoutSavingLabel}
        >
            {enableRouteBlocking && <UnsavedChangesRouteBlocker when={guardConfig.isDirty} />}
            {children}
        </UnsavedChangesGuardProvider>
    );
}
